// client/src/reservations/hooks/useBusinessSocket.js
import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

export default function useBusinessSocket(businessId, seedLive = {}) {
  const [live, setLive] = useState(seedLive); // keyed by kind → arrays of “blocks”
  const socketRef = useRef(null);
  // Normalize various time/date representations into a stable key component
  function normTs(v) {
    if (!v) return '';
    // Accept Date, number, or string; fall back to string if unparsable
    try {
      const t = (v instanceof Date) ? v.getTime() : new Date(v).getTime();
      return Number.isFinite(t) ? String(t) : String(v);
    } catch {
      return String(v);
    }
  }
  function normDateYYYYMMDD(v) {
    if (!v) return '';
    try {
      const d = (v instanceof Date) ? v : new Date(v);
      if (!Number.isFinite(d.getTime())) return String(v);
      return d.toISOString().slice(0,10);
    } catch {
      return String(v);
    }
  }
  function keyFor(kind, x) {
    if (!x) return '';
    switch (kind) {
      case 'ps_service':
        // Use epoch ms to avoid timezone/format mismatches between seed and events
        return `${x.employee_id ?? ''}|${normTs(x.slot_start)}|${normTs(x.slot_end)}`;
      case 'cb_class':
        return `${x.class_session_id ?? ''}|${normDateYYYYMMDD(x.class_session_date)}`;
      case 'ms_table_type':
        return `${x.ms_seating_config_id ?? ''}|${x.ms_area_type ?? ''}|${normTs(x.slot_start)}|${normTs(x.slot_end)}`;
      case 'ac_room_type': {
        // nights like ["2025-08-20","2025-08-22")
        const m = String(x.nights || '').match(/(\d{4}-\d{2}-\d{2}).*?(\d{4}-\d{2}-\d{2})/);
        const a = m ? m[1] : '';
        const b = m ? m[2] : '';
        return `${x.offering_id || ''}|${a}|${b}`;
      }
      default:
        return JSON.stringify(x);
    }
  }

  // When seedLive updates after mount (common: bootstrap finishes later),
  // merge it into the current live state using the same identity keys.
  useEffect(() => {
    if (!seedLive || typeof seedLive !== 'object') return;
    const kinds = ['ps_service', 'cb_class', 'ms_table_type', 'ac_room_type'];
    setLive(prev => {
      const next = { ...prev };
      for (const kind of kinds) {
        const seedArr = Array.isArray(seedLive[kind]) ? seedLive[kind] : [];
        if (!seedArr.length) {
          // ensure array shape exists
          next[kind] = next[kind] || [];
          continue;
        }
        const map = new Map((next[kind] || []).map(x => [keyFor(kind, x), x]));
        seedArr.forEach(x => { const k = keyFor(kind, x); if (k) map.set(k, x); });
        next[kind] = Array.from(map.values());
      }
      return next;
    });
  }, [seedLive]);

  function upsert(kind, payload) {
    try { console.log('[ws] upsert', { kind, key: keyFor(kind, payload), payload }); } catch {}
    setLive(prev => {
      const arr = prev[kind] || [];
      const k = keyFor(kind, payload);
      const next = k
        ? [...arr.filter(item => keyFor(kind, item) !== k), payload]
        : [...arr, payload];
      return { ...prev, [kind]: next };
    });
  }

  function remove(kind, payload) {
    try { console.log('[ws] remove', { kind, key: keyFor(kind, payload), payload }); } catch {}
    setLive(prev => {
      const arr = prev[kind] || [];
      const k = keyFor(kind, payload);
      return { ...prev, [kind]: arr.filter(item => keyFor(kind, item) !== k) };
    });
  }
  useEffect(() => {
    // Prefer explicit env override; otherwise use backend port in dev, same-origin in prod.
    let baseUrl = (typeof process !== 'undefined' && process.env && (process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL)) || null;
    if (!baseUrl) {
      const isProd = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
      baseUrl = isProd ? '/' : 'http://localhost:5002';
    }
    const socket = io(baseUrl, { transports: ['websocket', 'polling'], withCredentials: true });
    socketRef.current = socket;
    const join = () => socket.emit('join.business', businessId);
    socket.on('connect', join);
    socket.on('reconnect', join);
    socket.on('connect_error', (err) => {
      console.warn('[ws] connect_error', err?.message || err);
    });
    socket.on('error', (err) => {
      console.warn('[ws] error', err?.message || err);
    });
    socket.on('disconnect', (reason) => {
      console.warn('[ws] disconnect', { reason });
    });
    join();
    function clearByCart(cart_id) {
      try { console.log('[ws] hold.released/converted -> clearByCart', { cart_id }); } catch {}
      setLive(prev => {
        const next = {};
        for (const [k, arr] of Object.entries(prev)) {
          next[k] = arr.filter(x => x.cart_id !== cart_id);
        }
        return next;
      });
    }

    socket.on('hold.created', ({ kind, item }) => {
      try { console.log('[ws] event hold.created', { kind, item }); } catch {}
      upsert(kind, item);
    });
    socket.on('hold.released', ({ cart_id }) => {
      try { console.log('[ws] event hold.released', { cart_id }); } catch {}
      clearByCart(cart_id);
    });
    socket.on('hold.converted', ({ cart_id }) => {
      try { console.log('[ws] event hold.converted', { cart_id }); } catch {}
      clearByCart(cart_id);
    });
    socket.on?.('reservation.created', ({ kind, item }) => {
      try { console.log('[ws] event reservation.created', { kind, item }); } catch {}
      upsert(kind, item);
    });
    socket.on?.('reservation.cancelled', ({ kind, item }) => {
      try { console.log('[ws] event reservation.cancelled', { kind, item }); } catch {}
      remove(kind, item);
    });

    return () => { socket.disconnect(); };
  }, [businessId]);

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      setLive(prev => {
        const prune = (arr=[]) => arr.filter(x => !x.expires_at || new Date(x.expires_at).getTime() > now);
        return {
          ps_service:   prune(prev.ps_service),
          cb_class:     prune(prev.cb_class),
          ms_table_type:prune(prev.ms_table_type),
          ac_room_type: prune(prev.ac_room_type),
        };
      });
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  return live;
}
